import React from 'react';
import classes from './EditTab.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import api from 'api';
import { useTranslation } from 'react-i18next';
import { IDocument } from 'interfaces';
import Spinner from 'components/Spinner/Spinner';
import { ExceptionOutlined } from '@ant-design/icons';
import DraftSelectionModal from 'components/Modals/DraftSelectionModal/DraftSelectionModal';
import cn from 'classnames';
import { installCollaboraShortcutBridge } from 'helpers/collaboraShortcutBridge';
import { Layout } from 'antd';
import AnalysisSider from '../AnalysisSider/AnalysisSider';
import { setCurrentEditorToken, setIsOpenAIAssistanceSider } from 'store/slices/aiFunctionsDataSlice';
import AIAssistanceSider from '../AIAssistanceSider/AIAssistanceSider';
import { setSelectedDocument } from 'store/slices/dataDocumentsSlice';
import { setIsUploadingSomeChat } from 'store/slices/windowStateSlice';

const baseURL = window.REACT_APP_SERVER_API !== 'REPLACE_REACT_APP_SERVER_API' ? window.REACT_APP_SERVER_API : process.env.REACT_APP_SERVER_API || 'http://localhost:8000/api';
const isAvailableEditing = window.REACT_APP_IS_EDITING_ACTIVE !== 'REPLACE_REACT_APP_IS_EDITING_ACTIVE' ? window.REACT_APP_IS_EDITING_ACTIVE : process.env.REACT_APP_IS_EDITING_ACTIVE || 'false';

type EditTabProps = {
  is_temp_link?: boolean;
  password?: string;
  is_download?: boolean;
};

const EditTab = ({is_temp_link, password, is_download}: EditTabProps) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const wrapRef = React.useRef<HTMLElement>(null);
  const uploadNewToken = React.useRef<boolean>(true);
  const shortcutBridgeCleanupRef = React.useRef<(() => void) | null>(null);

  const [text, setText] = React.useState('');

  const [editorLoaded, setEditorLoaded] = React.useState(false);
  const [isLoadingToken, setIsLoadingToken] = React.useState(true);
  const [docAccessToken, setDocAccessToken] = React.useState('');
  const [collaboraFilePath, setCollaboraFilePath] = React.useState('');
  const [showDraftModal, setShowDraftModal] = React.useState(false);
  const [draftInfo, setDraftInfo] = React.useState<{ has_draft: boolean; version_id: string | null; last_modified: string | null }>({ has_draft: false, version_id: null, last_modified: null });
  
  const { docsSegment, messageApi } = useAppSelector((state) => state.windowState);
  const { selectedDocument, selectedVersion } = useAppSelector((state) => state.documents);
  const { userData } = useAppSelector((state) => state.userData);

  const actualDocumentToView = selectedVersion ? selectedVersion : selectedDocument;

  const isExperimentalAvailable = userData?.is_experimental_available;

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const fetchToken = React.useCallback(async (documentId: string, isDraft: boolean, readonly?: boolean, signal?: AbortSignal) => {
    try {
      let token;
      if (is_temp_link) {
        token = await api.fetchTokenForTempLink({
          temp_link_id: documentId,
          readonly: true,
          is_draft: isDraft,
          is_agreement: false,
          locale: i18n.language,
          password: password,
          is_download,
        }, signal);        
        await api.incrementCounter(documentId)
      } else {
        token = await api.fetchEditToken({
          document_id: documentId,
          readonly: readonly || false,
          is_agreement: false,
          is_draft: isDraft,
          locale: i18n.language,
        }, signal);
      }

      let collaboraPathWithLocale = token.data?.editor_url_path + `&lang=${i18n.language}`;      
      const correctCollaboraPath = baseURL === 'http://localhost:8000/api' // для локальной разработки token.data?.editor_url_path.replace('localhost%3A9980%2', 'dev.athena-backend%3A8000%2')
        ? collaboraPathWithLocale.replace('localhost%3A9980%2', 'dev.athena-backend%3A8000%2') 
        : collaboraPathWithLocale
      setCollaboraFilePath(correctCollaboraPath);
      setDocAccessToken(token.data?.access_token);
      dispatch(setCurrentEditorToken(token.data?.access_token));
    } catch (e: any) {      
      setDocAccessToken(actualDocumentToView?.id!);
      if (e?.message !== 'canceled') {
        readonly
          ? messageApi?.error(t('Documents.details.editTab.view_error'))
          : messageApi?.error(t('Documents.details.editTab.edit_error'));
      }
    } finally {
      setIsLoadingToken(false);
    }
  }, [actualDocumentToView?.id, i18n.language, docsSegment]);

  const getToken = async (document: IDocument, signal: AbortSignal) => {
    setIsLoadingToken(true);
    try {
      if (docsSegment === 'view' && actualDocumentToView?.id && isAvailableEditing === 'true') {
        await fetchToken(document?.id, false, true, signal);
      } else {
        const draftExist = await api.hasDraft({document_id: document?.id});
        if (draftExist.data?.has_draft && draftExist.data?.version_id) {
          setDraftInfo({
            has_draft: true,
            version_id: draftExist.data.version_id,
            last_modified: draftExist.data.last_modified,
          });
          setShowDraftModal(true);
          setIsLoadingToken(false);
        } else {
          await fetchToken(document?.id, false, false, signal);
        }
      }
    } catch (e) {
      setDocAccessToken(actualDocumentToView?.id!);
      messageApi?.error(t('Documents.details.editTab.token_error'));
      setIsLoadingToken(false);
    }
  };

  const handleSelectDraft = React.useCallback(async () => {
    setShowDraftModal(false);
    setIsLoadingToken(true);
    if (draftInfo.version_id) {
      await fetchToken(draftInfo.version_id, true);
    }
  }, [draftInfo.version_id, fetchToken]);

  const handleSelectOriginal = React.useCallback(async () => {
    setShowDraftModal(false);
    setIsLoadingToken(true);
    if (actualDocumentToView?.id) {
      await fetchToken(actualDocumentToView.id, false);
    }
  }, [actualDocumentToView?.id, fetchToken]);

  const handleCancelModal = React.useCallback(() => {
    setShowDraftModal(false);
    setIsLoadingToken(false);
  }, []);

  React.useEffect(() => {

    const handlePostMessage = (event: MessageEvent<any>) => {
      // Handle messages from the Collabora Online iframe
      // For example, to detect when the editor is ready or when a save event occurs.
      let message = event.data;
      if (typeof event.data === 'string') {
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
      }

      if (message?.MessageId === 'App_LoadingStatus' && message?.Values?.Status === 'Document_Loaded') {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ MessageId: 'Host_PostmessageReady' }),
          '*'
        );
        setEditorLoaded(true);
        console.log('Collabora Online editor is ready.');
        shortcutBridgeCleanupRef.current?.();
        shortcutBridgeCleanupRef.current = installCollaboraShortcutBridge(iframeRef.current);
      }
      // Add more message handling as needed (e.g., saving document changes)

      if (message?.MessageId === 'Host_PostmessageReady') {
        setEditorLoaded(true);
      }
    };

    window.addEventListener('message', handlePostMessage);

    return () => {
      window.removeEventListener('message', handlePostMessage);
      shortcutBridgeCleanupRef.current?.();
      shortcutBridgeCleanupRef.current = null;
    };
  }, []);

  const controller = new AbortController();

  React.useEffect(() => {
    if (docsSegment === 'view' && actualDocumentToView?.id) {
      getToken(actualDocumentToView, controller.signal);
      uploadNewToken.current = true;
    } else if (docsSegment === 'edit' && selectedDocument?.id && uploadNewToken.current) {
      isExperimentalAvailable && dispatch(setIsOpenAIAssistanceSider({ open: true, width: 400 }));
      getToken(selectedDocument, controller.signal);
      uploadNewToken.current = false;
    }
  }, [actualDocumentToView?.id, docsSegment]);

  React.useEffect(() => {
    return () => {      
      setDocAccessToken('');
      setCollaboraFilePath('');
      setEditorLoaded(false);
      controller.abort();
      shortcutBridgeCleanupRef.current?.();
      shortcutBridgeCleanupRef.current = null;
      setEditorLoaded(false);
      dispatch(setCurrentEditorToken(null));
      dispatch(setIsUploadingSomeChat(false));
    }
  }, [docsSegment]);

  React.useEffect(() => {
    // Post the access token to the iframe once it's loaded
    if (editorLoaded && iframeRef.current && docAccessToken) {      
      const iframeWindow = iframeRef.current.contentWindow;
      if (iframeWindow) {
        // Send the access token to the iframe using PostMessage
        iframeWindow.postMessage(
          JSON.stringify({
            MessageId: 'Host_AccessToken', // Or another appropriate message ID based on WOPI
            Values: {
              access_token: docAccessToken,
            },
          }),
          '*' // In a real application, specify the target origin for security
        );
      }
    }
  }, [editorLoaded, docAccessToken]);

  React.useEffect(() => {
    if (!docAccessToken || !iframeRef.current || (!editorLoaded)) return;

    const iframeWindow = iframeRef.current.contentWindow;
    if (!iframeWindow) return;

    const hideCommand = (id: string) => {
      iframeWindow.postMessage(JSON.stringify({ MessageId: 'Remove_Button', Values: { id } }), '*');
      iframeWindow.postMessage(JSON.stringify({ MessageId: 'Hide_Menu_Item', Values: { id } }), '*')
    };

    hideCommand('about');
    hideCommand('online-help');
    hideCommand('serveraudit');
    hideCommand('server-audit');
    hideCommand('help-onlinehelp-break');
    hideCommand('help-serveraudit-break');

  }, [docAccessToken, editorLoaded]);
  
  const correctWindow = {
    true: (
      <iframe
        title='edit_window'
        ref={iframeRef}
        src={collaboraFilePath}
        allowFullScreen
        allow='clipboard-read *; clipboard-write *'
        sandbox='allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation allow-top-navigation-by-user-activation'
      />
    ),
    false: (
      <div className={classes.error}>
        <ExceptionOutlined className={classes.icon}/>
        <span className={classes.text}>{t('Documents.details.editTab.unavailable')}</span>
      </div>
    )
  };

  const search = (origin: string) => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      console.log(iframeWindow, '<<iframeWindow');
      
      const payload = {
        MessageId: 'Send_UNO_Command',
        args: {
          command: '.uno:ExecuteSearch',
          arguments: [
            { name: 'SearchItem.SearchString', value: text },
            { name: 'SearchItem.Backward', value: false },
           { name: "SearchItem.Command", value: 3 }
          ]
        }
      };

      iframeWindow.postMessage(payload, origin);
    }
  }

  const toText = (origin: string) => {
    console.log(text, '<<text');
    console.log(origin, '<<origin');
    
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      const payload = {
        MessageId: 'Action_Find',
        Values: {
          SearchItem: {
            SearchString: { type: 'string', value: text },
            Backward: { type: 'boolean', value: false },
            FindAll: { type: 'boolean', value: false }
          }
        }
      };

      iframeWindow.postMessage(payload, origin);
    }
  }

  const find = (origin: string) => {
    console.log(text, '<<text');
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      const payload = {
        MessageId: 'Action_FindReplace',
        Values: {
          SearchItem: {
            SearchString: { type: 'string', value: text },
            Backward: { type: 'boolean', value: false },
            FindAll: { type: 'boolean', value: false }
          }
        }
      };

      iframeWindow.postMessage(payload, origin);
    }
  }
  
  return (
    <>
      <Layout
        className={cn(
          classes.editWrap,
          is_temp_link ? classes.tempLinkHeight : classes.viewHeight
        )}
        ref={wrapRef}
      >
        {isLoadingToken
          ? <Spinner size={60} withLogo={true}/>
          : correctWindow[String(Boolean(docAccessToken)) as keyof typeof correctWindow]
        }
        <AnalysisSider wrapRef={wrapRef}/>

        {isExperimentalAvailable && docsSegment === 'edit' && <AIAssistanceSider />}
        
        {/* {userData?.email === 's@s.ru' && <div style={{position: 'absolute', bottom: 0, right: 0}}>
          <div style={{display: 'flex'}}>
            <Input value={text} onChange={(val) => setText(val.target.value)}/>
          </div>
          <div style={{display: 'flex'}}>
            <Button onClick={() => search('*')}>uno *</Button>
            <Button onClick={() => search(window.location.origin)}>uno location</Button>
          </div>
          <div style={{display: 'flex'}}>
            <Button onClick={() => find('*')}>Replace *</Button>
            <Button onClick={() => find(window.location.origin)}>Replace location</Button>
          </div>
          <div style={{display: 'flex'}}>
            <Button onClick={() => toText('*')}>Find *</Button>
            <Button onClick={() => toText(window.location.origin)}>Find location</Button>
          </div>
        </div>} */}
      </Layout>
      <DraftSelectionModal
        open={showDraftModal}
        onSelectDraft={handleSelectDraft}
        onSelectOriginal={handleSelectOriginal}
        onCancel={handleCancelModal}
        lastModified={draftInfo.last_modified}
      />
    </>
  );
}

export default EditTab;
